import { ChangeDetectionStrategy, Component, forwardRef, input, OnDestroy } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';

import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';

import moment, { Moment } from 'moment';
import { DatePickerValue, DateRangeValue } from './date-picker.models';
import { MatNativeDateModule } from '@angular/material/core';

let nextId = 0;

@Component({
  selector: 'app-date-picker',
  imports: [
    ReactiveFormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
  ],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
    provideMomentDateAdapter(),
  ],
})
export class DatePickerComponent implements ControlValueAccessor, OnDestroy {
  readonly label = input('Date');
  readonly placeholder = input('Choose a date');
  readonly min = input<Moment | null>(null);
  readonly max = input<Moment | null>(null);
  readonly type = input<'single' | 'range'>('single');

  readonly startDateControl = new FormControl<Moment | null>(null);
  readonly endDateControl = new FormControl<Moment | null>(null);

  value: DatePickerValue = null;
  disabled = false;

  inputId = `picker-${nextId++}`;

  private startDateSubscription = this.startDateControl.valueChanges.subscribe((start) => {
    if (this.type() !== 'range') {
      return;
    }

    this.updateRange(start, this.endDateControl.value);
  });

  private endDateSubscription = this.endDateControl.valueChanges.subscribe((end) => {
    if (this.type() !== 'range') {
      return;
    }

    this.updateRange(this.startDateControl.value, end);
  });

  private onChange: (value: DatePickerValue) => void = () => {
    /* empty */
  };

  private onTouched: () => void = () => {
    /* empty */
  };

  writeValue(value: DatePickerValue): void {
    this.value = value;

    if (this.type() !== 'range') {
      this.startDateControl.setValue(this.isDate(value) ? value : null, { emitEvent: false });

      return;
    }

    const range = this.isRange(value) ? value : null;

    this.startDateControl.setValue(range?.start ?? null, { emitEvent: false });

    this.endDateControl.setValue(range?.end ?? null, { emitEvent: false });
  }

  registerOnChange(fn: (value: DatePickerValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (isDisabled) {
      this.startDateControl.disable({ emitEvent: false });
      this.endDateControl.disable({ emitEvent: false });
    } else {
      this.startDateControl.enable({ emitEvent: false });
      this.endDateControl.enable({ emitEvent: false });
    }
  }

  onDateChange(event: MatDatepickerInputEvent<Moment>): void {
    if (this.type() !== 'single') {
      return;
    }

    this.value = event.value ?? null;

    this.onChange(this.value);
    this.onTouched();
  }

  handleBlur(): void {
    this.onTouched();
  }

  private updateRange(start: Moment | null, end: Moment | null): void {
    const value: DateRangeValue = {
      start,
      end,
    };

    this.value = value;
    this.onChange(value);
  }

  private isDate(value: DatePickerValue): value is Moment {
    return moment.isMoment(value);
  }

  private isRange(value: DatePickerValue): value is DateRangeValue {
    return value !== null && !moment.isMoment(value) && 'start' in value && 'end' in value;
  }

  ngOnDestroy(): void {
    this.startDateSubscription.unsubscribe();
    this.endDateSubscription.unsubscribe();
  }
}
